/**
 * Cache security event handling
 * Handles security events, violations, and threat detection
 */

import { SecurityEvent } from '../permission-integrity';
import { permissionAudit } from '../permission-audit';
import { systemLogger } from '@/lib/logging';
import type { CacheEntry } from '../types/cache-types';

export class CacheSecurity {
  private securityEventListeners: Array<(_event: SecurityEvent) => void> = [];

  /**
   * Setup security event listener for cache invalidation
   */
  setupSecurityEventListener(cache: Map<string, CacheEntry>): void {
    const handleSecurityEvent = (event: SecurityEvent) => {
      this.handleSecurityEvent(event, cache);
    };

    // Listen for permission integrity events
    if (typeof window !== 'undefined') {
      window.addEventListener('permission-security-event', handleSecurityEvent as unknown as (_event: Event) => void);
    }

    this.securityEventListeners.push(handleSecurityEvent);
  }

  /**
   * Handle security event
   */
  private handleSecurityEvent(event: SecurityEvent, cache: Map<string, CacheEntry>): void {
    systemLogger.warn('Security event detected, invalidating cache', {
      eventType: event.type,
      severity: event.severity,
      affectedCache: cache.size,
    });

    // Clear cache on security events to ensure fresh permission checks
    cache.clear();

    // Log security event for audit
    permissionAudit.logSecurityEvent({
      type: 'SUSPICIOUS_ACTIVITY',
      severity: 'LOW',
      details: {
        action: 'cache_cleared',
        securityEvent: event,
        cacheSize: cache.size,
      },
      timestamp: Date.now().toString(),
      source: 'cache-security',
    });
  }

  /**
   * Handle security error
   */
  handleSecurityError(
    error: Error,
    context: string,
    userId: string,
    cache: Map<string, CacheEntry>
  ): void {
    systemLogger.error(`Cache security error in ${context}:`, {
      error: error.message,
      userId,
      cacheSize: cache.size,
    });

    // Clear user's cache entries on security error
    this.clearUserCache(userId, cache);

    // Create security event
    const securityEvent: SecurityEvent = {
      type: 'SUSPICIOUS_ACTIVITY',
      severity: 'HIGH',
      details: {
        error: error.message,
        context,
        userId,
      },
      timestamp: Date.now().toString(),
      source: 'cache-security',
    };

    // Dispatch security event
    this.dispatchSecurityEvent(securityEvent);

    // Log for audit
    permissionAudit.logSecurityEvent({
      type: 'SUSPICIOUS_ACTIVITY',
      severity: 'HIGH',
      details: {
        securityEvent,
        userId,
        context,
      },
      timestamp: Date.now().toString(),
      source: 'cache-security',
    });
  }

  /**
   * Clear cache entries for specific user
   */
  private clearUserCache(userId: string, cache: Map<string, CacheEntry>): void {
    let removedCount = 0;

    for (const [key, entry] of cache.entries()) {
      if (entry.userId === userId) {
        cache.delete(key);
        removedCount++;
      }
    }

    systemLogger.info(`Cleared ${removedCount} cache entries for user ${userId} due to security event`);
  }

  /**
   * Dispatch security event to listeners
   */
  private dispatchSecurityEvent(event: SecurityEvent): void {
    // Notify registered listeners
    this.securityEventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        systemLogger.error('Error in security event listener:', error);
      }
    });

    // Dispatch to window if available (for other components)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('permission-security-event', { detail: event }));
    }
  }

  /**
   * Validate cache access permissions
   */
  validateCacheAccess(userId: string, cacheKey: string): boolean {
    // Basic validation - ensure user can only access their own cache entries
    // Cache keys should be scoped to user
    return cacheKey.includes(userId) || cacheKey.startsWith(`user:${userId}:`);
  }

  /**
   * Detect suspicious cache patterns
   */
  detectSuspiciousActivity(
    userId: string,
    accessPattern: { timestamp: number; key: string }[],
    _cache: Map<string, CacheEntry>
  ): SecurityEvent | null {
    // Check for rapid access patterns (potential brute force)
    const recentAccesses = accessPattern.filter(
      access => Date.now() - access.timestamp < 60000 // Last minute
    );

    if (recentAccesses.length > 50) {
      return {
        type: 'SUSPICIOUS_ACTIVITY',
        severity: 'MEDIUM',
        details: {
          userId,
          accessCount: recentAccesses.length,
          timeWindow: '1 minute',
        },
        timestamp: Date.now().toString(),
        source: 'cache-security',
      };
    }

    // Check for access to many different cache keys (potential enumeration)
    const uniqueKeys = new Set(recentAccesses.map(access => access.key));
    if (uniqueKeys.size > 20) {
      return {
        type: 'SUSPICIOUS_ACTIVITY',
        severity: 'MEDIUM',
        details: {
          userId,
          uniqueKeyCount: uniqueKeys.size,
          timeWindow: '1 minute',
        },
        timestamp: Date.now().toString(),
        source: 'cache-security',
      };
    }

    return null;
  }

  /**
   * Cleanup security event listeners
   */
  cleanup(): void {
    this.securityEventListeners = [];

    if (typeof window !== 'undefined') {
      // Remove event listeners (would need references to specific handlers)
      // This is a simplified cleanup
    }
  }
}